import React, { FC, useState } from 'react';
import {
  Box,
  Stack,
  Dialog,
  Tooltip,
  Divider,
  TextField,
  IconButton,
  Typography,
  DialogTitle,
  DialogContent,
  CircularProgress,
} from '@mui/material';
import {
  Add,
  Close,
  Remove,
  DarkMode,
  Download,
  LightMode,
  Visibility,
  RestartAlt,
  NavigateNext,
  VisibilityOff,
  NavigateBefore,
} from '@mui/icons-material';
import { Page, pdfjs, Document } from 'react-pdf';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { saveAs } from 'file-saver';
import { ShowIf } from '@utils/index';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString();

interface PDFViewerProps {
  file?: string;
  fileName?: string;
  fileSize?: number;
  icon?: JSX.Element;
  embedded?: boolean;
  containerRef?: React.RefObject<HTMLDivElement>;
  validatePDF?: boolean;
  downloadEnabled?: boolean;
}

const PDFViewer: FC<PDFViewerProps> = ({
  file,
  fileName,
  fileSize,
  icon = <Visibility />,
  embedded = false,
  containerRef,
  validatePDF = true,
  downloadEnabled = true,
}) => {
  const [open, setOpen] = useState(false);
  const [numPages, setNumPages] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [urlPDF, setURLPDF] = useState<string | null>(null);
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [scale, setScale] = useState(1.4);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [pageInputValue, setPageInputValue] = useState('1');

  const fileSizeLimit = 7 * 1024 * 1024;

  const handleOpen = () => {
    if (!file) {
      enqueueSnackbar(t('Error: File not found.', { variant: 'info' }));
      return;
    }

    if (validatePDF) {
      const isPDF = fileName?.toLowerCase().endsWith('.pdf') || file.startsWith('blob:');
      if (fileSize && (fileSize > fileSizeLimit || !isPDF)) {
        enqueueSnackbar(t('File size limit exceeded or invalid format'), { variant: 'info' });
        return;
      }
    }

    const url = decodeURIComponent(file);
    setURLPDF(url);
    setLoading(true);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setURLPDF(null);
  };

  const onDocumentLoadSuccess = ({ numPages: totalNumPages }: { numPages: number }) => {
    setNumPages(totalNumPages);
    setLoading(false);
  };

  const handleZoomIn = () => setScale((prev) => Math.min(prev + 0.2, 3));
  const handleZoomOut = () => setScale((prev) => Math.max(prev - 0.2, 0.5));
  const handleResetZoom = () => setScale(1.4);

  const scrollToPage = (pageNumber: number) => {
    setTimeout(() => {
      const pageElement = document.getElementById(`page_${pageNumber}`);
      if (pageElement) {
        pageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const handlePageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setPageInputValue(value);

    const pageNumber = parseInt(value, 10);
    if (!Number.isNaN(pageNumber) && pageNumber >= 1 && pageNumber <= (numPages || 1)) {
      handlePageChange(pageNumber);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= (numPages || 1)) {
      setCurrentPage(newPage);
      setPageInputValue(newPage.toString());
      if (!embedded) {
        scrollToPage(newPage);
      }
    }
  };

  const handleDownload = async () => {
    if (!file) {
      enqueueSnackbar(t('Error: File not found.'), { variant: 'error' });
      return;
    }

    if (!downloadEnabled) {
      enqueueSnackbar(t('You do not have permission to access this page'), { variant: 'error' });
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(decodeURIComponent(file));
      const blob = await response.blob();
      saveAs(blob, fileName || 'document.pdf');
    } catch (error) {
      enqueueSnackbar(t('Error downloading the file.'), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (embedded && containerRef) {
      setShowPreview(!showPreview);
    } else {
      handleOpen();
    }
  };

  const renderEmbeddedControls = () => (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="center"
        justifyContent="flex-end"
        sx={{ borderBottom: 1, borderColor: 'divider', pb: 1 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <NavigateBefore />
          </IconButton>

          <TextField
            type="text"
            size="small"
            value={pageInputValue}
            onChange={handlePageInputChange}
            sx={{ width: 70 }}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
            }}
          />

          <Typography variant="body2">/ {numPages}</Typography>

          <IconButton
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === numPages}
          >
            <NavigateNext />
          </IconButton>
        </Stack>

        <Divider orientation="vertical" flexItem />

        <Stack direction="row" spacing={1}>
          <IconButton onClick={handleZoomOut}>
            <Remove />
          </IconButton>
          <IconButton onClick={handleResetZoom}>
            <RestartAlt />
          </IconButton>
          <IconButton onClick={handleZoomIn}>
            <Add />
          </IconButton>
        </Stack>
      </Stack>

      {/* Contenido del PDF */}
      <Box sx={{ display: 'grid', justifyContent: 'center' }}>
        {loading && <CircularProgress />}
        <Document file={file} onLoadSuccess={onDocumentLoadSuccess}>
          <Page
            pageNumber={currentPage}
            renderMode="canvas"
            renderTextLayer={false}
            renderAnnotationLayer={false}
            scale={scale}
          />
        </Document>
      </Box>
    </Stack>
  );

  return (
    <>
      <Tooltip title={t('Preview')}>
        <IconButton onClick={handleClick} sx={{ position: 'relative', zIndex: 1 }}>
          {(embedded && showPreview) || open ? <VisibilityOff /> : icon}
        </IconButton>
      </Tooltip>

      {/* Preview embebido con controles */}
      {embedded &&
        showPreview &&
        containerRef &&
        containerRef.current &&
        ReactDOM.createPortal(renderEmbeddedControls(), containerRef.current)}

      <Dialog
        fullWidth
        maxWidth="md"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            bgcolor: isDarkMode ? 'grey.900' : 'background.paper',
            color: isDarkMode ? 'common.white' : 'text.primary',
            '& .react-pdf__Page__canvas': {
              filter: isDarkMode ? 'invert(1)' : 'none',
            },
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', color: 'inherit' }}>
          {fileName ? `${fileName} ${t('Preview')}` : t('Preview')}
          <IconButton
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'inherit',
            }}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: isDarkMode ? 'grey.900' : 'background.paper',
            borderBottom: '1px solid',
            borderColor: isDarkMode ? 'grey.800' : 'divider',
            py: 1,
            color: 'inherit',
          }}
        >
          <Stack direction="row" spacing={2} sx={{ justifyContent: 'center' }}>
            <Stack direction="row" spacing={1}>
              <IconButton onClick={() => setIsDarkMode(!isDarkMode)} sx={{ color: 'inherit' }}>
                {isDarkMode ? <LightMode /> : <DarkMode />}
              </IconButton>

              <ShowIf condition={downloadEnabled}>
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ bgcolor: isDarkMode ? 'grey.800' : 'divider' }}
                />
                <Tooltip title={t('Download PDF')} arrow>
                  <IconButton onClick={handleDownload} sx={{ color: 'inherit' }}>
                    <Download />
                  </IconButton>
                </Tooltip>
              </ShowIf>
            </Stack>
          </Stack>
        </Box>

        <DialogContent
          sx={{
            display: 'grid',
            justifyContent: 'center',
            alignItems: 'center',
            pt: 1,
          }}
        >
          {urlPDF && (
            <Box sx={{ scrollBehavior: 'smooth' }}>
              <Document file={urlPDF} onLoadSuccess={onDocumentLoadSuccess}>
                {Array.from(new Array(numPages || 0), (_, index) => (
                  <Box key={`page_${index + 1}`} id={`page_${index + 1}`} sx={{ mb: 2 }}>
                    <Page
                      pageNumber={index + 1}
                      renderMode="canvas"
                      renderTextLayer={false}
                      renderAnnotationLayer={false}
                      scale={scale}
                    />
                  </Box>
                ))}
              </Document>
            </Box>
          )}
          {!numPages && !loading && <Typography>{t('Failed to load the PDF.')} </Typography>}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PDFViewer;
